import { array, boolean, number, NumberSchema, object, string } from 'yup'

import { Id, RequestTicket, RequestTicketClassType, Service } from 'src/types'

import { stringify } from 'src/core/hooks/use-translation'

type GetValidationSchemaProps = {
  findService(_: Id): Service
  force_image: boolean
  kmNeedJustification: boolean
  useGasValue: boolean
}

// the position reflects if an error message occurs to the visitor
export enum ReceiptImageFileTypeAvailable {
  JPEG = 'image/jpeg',
  JPG = 'image/jpg',
  PNG = 'image/png',
  PDF = 'application/pdf',
  GIF = 'image/gif',
  BMP = 'image/bmp',
  WEBp = 'image/webp',
  TIFF = 'image/tiff'
}

export function getInitialValues() {
  return {
    active: true,
    class_type: RequestTicketClassType.EXPENSE,
    cost: 0,
    request_id: null,
    request_ticket_id: null,
    request_ticket_number: null,
    request_ticket_receipts: [
      {
        active: true,
        amount: 0,
        billed: false,
        cost: 0,
        currency_id: '',
        date: 0,
        exchange_rate: 1,
        gas_value: 0,
        invoice: '',
        justification: '',
        payment_resource_id: '',
        quantity: 0,
        reference_id: '',
        request_ticket_receipt_id: null,
        request_ticket_receipt_images: [],
        request_ticket_receipt_number: null,
        request_ticket_receipt_tracks: [],
        service_id: ''
      }
    ],
    request_ticket_title_advances: [],
    request_ticket_type: RequestTicketClassType.EXPENSE,
    request_ticket_user_data: [],
    status: 'APPROVAL_PENDING',
    user_id: null
  } as RequestTicket
}

export function getValidationSchema({
  findService,
  force_image,
  kmNeedJustification,
  useGasValue
}: GetValidationSchemaProps) {
  return object().shape({
    request_ticket_receipts: array()
      .of(
        object().shape({
          amount: number().when(
            'service_id',
            (service_id: Id, schema: NumberSchema) => {
              const foundService = findService(service_id)
              if (foundService?.standard_type !== 'KM') {
                return schema
                  .required()
                  .positive('web.services.advances.form.amount.placeholder')
              }

              return schema
            }
          ),
          currency_id: string().required(),
          date: number().required(),
          exchange_rate: number().when(
            'service_id',
            (service_id: Id, schema: NumberSchema) => {
              const foundService = findService(service_id)

              if (foundService?.service_type === 'VALUE') {
                return schema
                  .required()
                  .positive(
                    'web.services.advances.form.exchange-rate.placeholder'
                  )
              }
              return schema
            }
          ),
          gas_value: number().when(
            'service_id',
            (service_id: Id, schema: NumberSchema) => {
              const foundService = findService(service_id)
              if (useGasValue && foundService?.standard_type === 'KM') {
                return schema
                  .required()
                  .positive(
                    'web.services.advances.form.exchange-rate.placeholder'
                  )
              }

              return schema
            }
          ),
          payment_resource_id: string().required().typeError(),
          quantity: number().when(
            'service_id',
            (service_id: Id, schema: NumberSchema) => {
              const foundService = findService(service_id)

              if (useGasValue && foundService?.standard_type === 'KM') {
                return schema.required().min(
                  1,
                  stringify('common.yup.field-min-value', {
                    value: 1
                  })
                )
              }

              return schema
            }
          ),
          reference_id: string().required().typeError(),
          service_id: string().required().typeError(),
          request_ticket_receipt_images: array()
            .of(object({ active: boolean() }))
            .test(
              'active-image-validation',
              'web.receipts.policy-violated.need-force-image',
              (imagesArray: any) =>
                !(force_image && !imagesArray?.some((img: any) => img.active))
            ),
          justification: string().when(
            'service_id',
            (service_id: Id, schema: NumberSchema) => {
              if (!kmNeedJustification) {
                return schema
              }

              const foundService = findService(service_id)

              return foundService?.standard_type === 'KM'
                ? schema.required()
                : schema
            }
          )
        })
      )
      .required(),
    request_ticket_user_data: array().of(
      object().shape({
        traveler: object().shape({
          user_id: string().required()
        })
      })
    )
  })
}
